// 2.D.2 — Geneerinen vahvistus-modaali (poisto, bulk-actions, jne).
//
// Avautuu kun `open` = true. ESC-nappi ja overlay-click sulkevat (cancel).
// Confirm-painike on punainen jos `destructive=true` (poisto).

import { useEffect } from "react";

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  /** Selitysteksti — voi olla string tai ReactNode (esim. lista). */
  message: React.ReactNode;
  /** "Poista" / "Vahvista" — destructive-actioneille tyypillisesti "Poista". */
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** isProcessing kytkee Confirm-painikkeen pois ja nayttaa "Kasitellaan..." */
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Vahvista",
  cancelLabel = "Peruuta",
  destructive = false,
  isProcessing = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isProcessing) onCancel();
      if (e.key === "Enter" && !isProcessing) onConfirm();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, isProcessing, onConfirm, onCancel]);

  if (!open) return null;

  const confirmCls = destructive
    ? "rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
    : "rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white disabled:opacity-50";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h3 className="text-lg font-semibold tracking-tight text-slate-100">{title}</h3>
        <div className="mt-3 text-sm text-slate-300">{message}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={confirmCls}
          >
            {isProcessing ? "Kasitellaan…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
